import type {
  EditOrgActionRequest,
  GetOrgActionResponse,
} from 'components/IAP/hooks/orgActionHooks';
import { formatActionDisplayId } from 'components/IAP/utils/actionDisplayId';
import { buildGeoLocationFromGeoJson } from 'components/GeoLocationComponent/buildGeoLocationFromGeoJson';
import {
  formatStoreListLabel,
  HELENE_STORE_CHECKLIST_LABELS,
  storesInCone,
  type CfaDemoStore,
} from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/historicalHurricanesHeleneData';

const STORAGE_KEY = 'cfa-helene-demo-org-actions-v1';
const FIELD_STORAGE_KEY = 'cfa-helene-demo-org-action-fields-v1';

export const HELENE_DEMO_ACTION_ID_PREFIX = 'demo-helene-cfa-action-';

export function isHeleneDemoActionId(id: string | null | undefined): boolean {
  return !!id && id.startsWith(HELENE_DEMO_ACTION_ID_PREFIX);
}

type StoredChecklist = Record<string, { label: string; checked: boolean }[]>;

type StoredFields = {
  assignee_kind?: string | null;
  assignee_id?: string | null;
  poc?: string | null;
  status?: string | null;
  priority?: string | null;
  target_date?: string | null;
  briefed_poc?: boolean;
  geolocation?: GetOrgActionResponse['geolocation'];
};

function storeGeolocation(store: CfaDemoStore) {
  const geometry = {
    type: 'Point' as const,
    coordinates: [store.lon, store.lat] as [number, number],
  };
  return buildGeoLocationFromGeoJson({
    geometry,
    centroid: {
      type: 'Feature',
      geometry,
      properties: {},
    },
    address: `${store.city}, ${store.state}`,
    placeName: store.name,
    existingId: `demo-geo-${store.id}`,
  });
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function readStoredChecklists(): StoredChecklist {
  return readJson<StoredChecklist>(STORAGE_KEY, {});
}

function writeStoredChecklists(data: StoredChecklist): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function readStoredFields(): Record<string, StoredFields> {
  return readJson<Record<string, StoredFields>>(FIELD_STORAGE_KEY, {});
}

function writeStoredFields(data: Record<string, StoredFields>): void {
  localStorage.setItem(FIELD_STORAGE_KEY, JSON.stringify(data));
}

function defaultChecklist(): { label: string; checked: boolean }[] {
  return HELENE_STORE_CHECKLIST_LABELS.map((label) => ({
    label,
    checked: false,
  }));
}

/**
 * One org action per store in the Helene cone; checklist persisted in
 * localStorage.
 */
export function getHeleneDemoOrgActions(
  groupGuid: string,
  userGuid: string
): GetOrgActionResponse[] {
  const stored = readStoredChecklists();
  const now = new Date('2024-09-26T19:00:00.000Z');

  const fieldsById = readStoredFields();

  return storesInCone().map((store, index) => {
    const actionId = `${HELENE_DEMO_ACTION_ID_PREFIX}${store.id}`;
    const checklist = stored[actionId] ?? defaultChecklist();
    const fields = fieldsById[actionId] ?? {};
    const allDone = checklist.every((c) => c.checked);
    const assigned = Boolean(fields.assignee_kind && fields.assignee_id);
    return {
      id: actionId as UUID,
      action_id: actionId as UUID,
      display_id: formatActionDisplayId('Chick-fil-A', index + 1),
      name: `Helene prep — ${formatStoreListLabel(store)}`,
      description:
        `Store ${store.name} (${store.city}, ${store.state}) is inside the Helene forecast cone. ` +
        `Complete the prep checklist, then mark tasks done as the store reports completion.`,
      checklist,
      resources: [`Store #${store.storeNumber}`, store.city],
      archived: false,
      poc: fields.poc ?? null,
      briefed_poc: fields.briefed_poc ?? false,
      start_date: now,
      status: fields.status ?? (allDone ? 'Closed' : 'Current/Ongoing'),
      target_date: fields.target_date
        ? new Date(fields.target_date)
        : new Date('2024-09-27T00:00:00.000Z'),
      actual_date: allDone ? now : null,
      group_guid: groupGuid as UUID,
      user_guid: userGuid as UUID,
      user_name: 'CFA Regional Ops (demo)',
      poc_user_name: assigned ? undefined : store.name,
      version: 1,
      timestamp: now,
      index,
      assignee_kind: (fields.assignee_kind ??
        null) as GetOrgActionResponse['assignee_kind'],
      assignee_id: fields.assignee_id ?? null,
      priority: fields.priority ?? undefined,
      geolocation: fields.geolocation ?? storeGeolocation(store),
    };
  });
}

export function updateHeleneDemoOrgAction(
  data: EditOrgActionRequest
): GetOrgActionResponse | null {
  if (!isHeleneDemoActionId(data.action_id)) return null;
  const stored = readStoredChecklists();
  if (data.checklist) {
    stored[data.action_id] = data.checklist.map((c) => ({
      label: c.label,
      checked: !!c.checked,
    }));
    writeStoredChecklists(stored);
  }
  const fieldsById = readStoredFields();
  const nextFields: StoredFields = { ...fieldsById[data.action_id] };
  if ('assignee_kind' in data)
    nextFields.assignee_kind = data.assignee_kind ?? null;
  if ('assignee_id' in data) nextFields.assignee_id = data.assignee_id ?? null;
  if ('poc' in data) nextFields.poc = data.poc ?? null;
  if ('status' in data && data.status != null) nextFields.status = data.status;
  if ('priority' in data && data.priority != null)
    nextFields.priority = data.priority;
  if ('target_date' in data) {
    nextFields.target_date = data.target_date
      ? new Date(data.target_date).toISOString()
      : null;
  }
  if ('briefed_poc' in data && data.briefed_poc != null) {
    nextFields.briefed_poc = data.briefed_poc;
  }
  if ('geolocation' in data) {
    nextFields.geolocation = data.geolocation ?? null;
  }
  fieldsById[data.action_id] = nextFields;
  writeStoredFields(fieldsById);
  // Rebuild a single action snapshot for callers that expect a return.
  const all = getHeleneDemoOrgActions('demo-group', 'demo-user');
  return all.find((a) => a.action_id === data.action_id) ?? null;
}
